import {
    useState,
    useEffect,
    type Reducer,
    type ReducerState,
    useReducer,
    type ReducerAction,
    useRef,
    useLayoutEffect,
    type Dispatch,
} from 'react';
import {BehaviorSubject} from 'rxjs';
import equals from 'is-equal-shallow';

import {isPrimitive, type Primitive} from 'web-app/util/shallow-diff';
import {hasValue} from '@famly/stat_ts-utils_has-value';

/**
 * Create a reducer that child components can subscribe to with a selector.
 * Subscribed components will only update when the return value
 * of the selector changes.
 *
 * @example
 * const {useSubscribableReducer, useSubscription} = createSubscribableReducer(myReducer, emptyState);
 *
 * RootComponent() {
 *  const [state, dispatch] = useSubscribableReducer();
 *  return (
 *    <ChildComponent />
 *  )
 * }
 *
 * ChildComponent() {
 *   const myValue = useSubscription(state => state.foo.bar.biz)
 *
 *   return ...
 * }
 *
 */
export const createSubscribableReducer = <R extends Reducer<any, any>>(reducer: R) => {
    let subject: BehaviorSubject<ReducerState<R>> | undefined;

    const getSubject = () => {
        if (!hasValue(subject)) {
            throw new Error('Subscriber mounted with no mounted reducer');
        }
        return subject;
    };

    const subscribe = (listener: (state: ReducerState<R>) => void) => {
        const subscription = getSubject().subscribe(listener);
        return () => subscription.unsubscribe();
    };

    const callSubscriptions = (state: ReducerState<R>) => getSubject().next(state);

    const useSubscribableReducer = (initialState: ReducerState<R>): [ReducerState<R>, Dispatch<ReducerAction<R>>] => {
        if (!hasValue(subject)) {
            subject = new BehaviorSubject(initialState);
        }

        const [state, dispatch] = useReducer(reducer, initialState);

        useEffect(() => {
            // Avoid throwing when there is no subject
            if (subject) {
                callSubscriptions(state);
            }
        }, [state]);

        useEffect(
            () => () => {
                // Remove subject when unmounting
                subject = undefined;
            },
            [],
        );

        return [state, dispatch];
    };

    /**
     * Note that if you use values outside the closure of selector or computer things
     * might not work as you expect!
     */
    function useSubscription<K>(selector: (state: ReducerState<R>) => K): K;
    function useSubscription<K, U>(selector: (state: ReducerState<R>) => K, computer: (selected: K) => U): U;
    function useSubscription<K, U>(selector: (state: ReducerState<R>) => K, computer?: (selected: K) => U) {
        /**
         * Keeping a reference to transformation functions so we only update
         * when store emits an update. This means that changes to your transformation
         * functions will _not_ (!!) trigger a new render.
         */
        const selectorRef = useRef(selector);
        const computerRef = useRef(computer);

        /**
         * By keeping a reference to the return value of selector we
         * can determine if the value needs to be computed again by comparing
         * the value of selected
         */
        const selected = useRef(selector(getSubject().value));

        const [computed, setComputed] = useState(hasValue(computer) ? computer(selected.current) : selected.current);

        useLayoutEffect(
            () =>
                subscribe(state => {
                    const newSelected = selectorRef.current(state);

                    // Only compute new values when the selected state changes
                    if (!isEqual(selected.current, newSelected)) {
                        selected.current = newSelected;
                        setComputed(
                            hasValue(computerRef.current) ? computerRef.current(selected.current) : selected.current,
                        );
                    }
                }),
            [],
        );

        return computed;
    }

    return {useSubscribableReducer, useSubscription};
};

const isEqual = <K extends {} | Primitive>(a: K, b: K) => {
    // If values are simple we can compare them with ===
    if (isPrimitive(a) || isPrimitive(b)) {
        return a === b;
    }

    // If values are objects we can use shallow-equal
    if (typeof a === 'object' && typeof b === 'object') {
        return equals(a, b);
    }

    // Otherwise we fall back to ===
    return a === b;
};
